import {
  mergeClasses,
  ProgressBar,
  Text,
  Caption1,
  Tooltip,
} from "@fluentui/react-components";
import {
  LockClosed16Regular,
  CloudArrowUp16Regular,
} from "@fluentui/react-icons";

import {
  IInterviewSidebarProps,
  ObjectiveState,
} from "./InterviewSidebar.types";
import { CSSProperties } from "react";
import { useStyles } from "./InterviewSidebar.styles";
import { designTokens } from "theme/customTheme";

const getObjectiveState = (
  order: number | undefined,
  currentObjectiveOrder: number,
): ObjectiveState => {
  if (order !== undefined && order < currentObjectiveOrder) return "completed";
  if (order === currentObjectiveOrder) return "active";
  return "upcoming";
};

export const InterviewSidebar = ({
  diagnosticName,
  objectives,
  currentObjectiveOrder,
  className,
  accentPalette,
}: IInterviewSidebarProps): JSX.Element => {
  const styles = useStyles();

  const total = objectives.length;
  const completedCount = objectives.filter(
    (o) => o.order !== undefined && o.order < currentObjectiveOrder,
  ).length;
  const progressValue = completedCount / (total || 1);

  const sidebarStyle = accentPalette
    ? {
        backgroundColor: accentPalette.sidebarBg,
        borderRight: `1px solid ${accentPalette.sidebarBorder}`,
      }
    : undefined;
  const footerStyle = accentPalette
    ? { borderTop: `1px solid ${accentPalette.sidebarBorder}` }
    : undefined;
  const progressBarStyle = accentPalette
    ? { backgroundColor: accentPalette.accent }
    : undefined;

  return (
    <nav
      className={mergeClasses(styles.sidebar, className)}
      style={sidebarStyle}
      aria-label="Interview objectives"
    >
      <Text
        as="p"
        weight="semibold"
        size={500}
        className={styles.diagnosticName}
      >
        {diagnosticName}
      </Text>

      <Caption1 as="span" className={styles.sectionLabel}>
        Objectives
      </Caption1>

      <Caption1 as="span" className={styles.progressCount}>
        {completedCount} of {total} completed
      </Caption1>

      <div className={styles.progressWrapper}>
        <ProgressBar
          value={progressValue}
          shape="rounded"
          thickness="large"
          aria-label="Interview progress"
          aria-valuenow={Math.round(progressValue * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          bar={progressBarStyle ? { style: progressBarStyle } : undefined}
        />
      </div>

      <ol className={styles.objectiveList} aria-label="Objectives list">
        {objectives.map((obj, idx) => {
          const state = getObjectiveState(obj.order, currentObjectiveOrder);
          const isActive = state === "active";
          const isCompleted = state === "completed";
          const isUpcoming = state === "upcoming";

          const itemStyle =
            accentPalette && isActive
              ? { backgroundColor: accentPalette.row }
              : undefined;
          const isFilled = isCompleted || isActive;
          const badgeStyle =
            accentPalette && isFilled
              ? {
                  backgroundColor: accentPalette.accent,
                  color: accentPalette.accentText,
                }
              : undefined;
          let chipStyle: CSSProperties | undefined;
          if (accentPalette && isCompleted) {
            chipStyle = {
              backgroundColor: accentPalette.tint40,
              border: `1px solid ${accentPalette.border}`,
              color: accentPalette.chipText,
            };
          } else if (accentPalette && isActive) {
            chipStyle = {
              backgroundColor: "transparent",
              border: "none",
              color: accentPalette.chipText,
            };
          }

          return (
            <li
              key={obj._id ?? idx}
              className={mergeClasses(
                styles.objectiveItem,
                isActive && styles.objectiveItemActive,
              )}
              style={{
                ...itemStyle,
                ["--obj-hover-bg" as string]:
                  accentPalette?.headerMedium ?? designTokens.primary[100],
              }}
              aria-current={isActive ? "true" : undefined}
            >
              <span
                className={mergeClasses(
                  styles.badge,
                  isFilled ? styles.badgeFilled : styles.badgeUpcoming,
                )}
                style={badgeStyle}
                aria-hidden="true"
              >
                {idx + 1}
              </span>

              <div className={styles.objectiveTextCol}>
                <Tooltip
                  content={obj.name ?? obj.text ?? ""}
                  relationship="label"
                  positioning="after"
                  withArrow
                >
                  <Caption1
                    as="span"
                    className={mergeClasses(
                      styles.objectiveName,
                      isActive && styles.objectiveNameActive,
                      isUpcoming && styles.objectiveNameUpcoming,
                    )}
                  >
                    {obj.name ?? obj.text}
                  </Caption1>
                </Tooltip>

                <span
                  className={mergeClasses(
                    styles.statusChip,
                    isCompleted && styles.statusChipCompleted,
                    isActive && styles.statusChipActive,
                    isUpcoming && styles.statusChipUpcoming,
                  )}
                  style={chipStyle}
                >
                  {isCompleted
                    ? "Completed"
                    : isActive
                      ? "In Progress"
                      : "Upcoming"}
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      <footer className={styles.footer} style={footerStyle}>
        <div className={styles.footerRow}>
          <LockClosed16Regular
            className={styles.footerIcon}
            aria-hidden="true"
          />
          <Caption1 as="span" className={styles.footerText}>
            Responses anonymized
          </Caption1>
        </div>
        <div className={styles.footerRow}>
          <CloudArrowUp16Regular
            className={styles.footerIcon}
            aria-hidden="true"
          />
          <Caption1 as="span" className={styles.footerText}>
            Saved · resume anytime
          </Caption1>
        </div>
      </footer>
    </nav>
  );
};
